import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { api, API_BASE_URL, formatDateCourte } from '../../api'
import { useCachedFetch } from '../../lib/swr'
import { modales } from '../../contexts/modales'
import { partagerImage } from '../../lib/partage'
import { SOUS_TITRE_DEFAUT, STICKERS, accrocheAleatoire } from '../../lib/accroches'

/**
 * « Créer un visuel » (S64) — l'image prete a poster en story ou en carre.
 *
 * POURQUOI : le PO veut que les producteurs partagent sur les reseaux une image
 * comme celles qu'ils bricolent a la main (photo, accroche, liste des produits,
 * rendez-vous au marche). Le serveur compose le PNG (`/public/producteurs/:id/
 * visuel`) ; ici on ne fait que choisir trois choses — le format, l'accroche,
 * les produits a montrer — et on tend l'image a la feuille de partage.
 *
 * Tout est pre-rempli depuis la fiche PUBLIQUE (cache SWR partage avec Ma page).
 * Aucun etat n'est sauvegarde : un visuel se refait en dix secondes.
 */

const FORMATS = [
  { v: 'story', l: 'Story', s: '9:16', ratio: '9 / 16' },
  { v: 'carre', l: 'Carré', s: '1:1', ratio: '1 / 1' },
]

const bordure = '1px solid rgb(var(--c-green) / 0.14)'
const champ = {
  width: '100%', fontSize: 16, padding: '11px 13px', borderRadius: 13, border: bordure,
  background: 'rgb(255 255 255 / 0.7)', color: 'rgb(var(--c-green))', boxSizing: 'border-box', fontFamily: 'inherit',
}
const etiquette = { fontSize: 11.5, fontWeight: 700, color: 'rgb(var(--c-green) / 0.6)', display: 'block', marginBottom: 5 }

/** Adresse du PNG pour les reglages courants (le serveur fait le reste). */
function urlVisuel({ id, format, accroche, sousTitre, sticker, evenement, produitsGardes, total }) {
  const p = new URLSearchParams({ format, accroche, sous: sousTitre, sticker })
  if (evenement) p.set('evenement', evenement)
  if (produitsGardes.length !== total) p.set('produits', produitsGardes.join(','))
  return `${API_BASE_URL}/public/producteurs/${id}/visuel?${p.toString()}`
}

/**
 * @param {string} [producteurIdInitial]  identifiant de l'organisation ; absent = celle du compte connecte
 * @param {string} [evenementId]          evenement pre-choisi (bouton sur une carte d'evenement)
 */
function ModaleVisuel({ producteurId: producteurIdInitial, evenementId, onClose }) {
  const { data: moi } = useCachedFetch('producer/me', api.producer.getMe, { enabled: !producteurIdInitial })
  const producteurId = producteurIdInitial || moi?.id
  const { data: fiche } = useCachedFetch(
    producteurId ? `producteur/${producteurId}` : null,
    () => api.getProducteur(producteurId),
    { enabled: !!producteurId },
  )
  const [format, setFormat] = useState('story')
  const [accroche, setAccroche] = useState(() => accrocheAleatoire())
  // Avec un evenement, le bandeau dit lieu · horaires tout seul : champ vide.
  const [evenement, setEvenement] = useState(evenementId || '')
  const [sousTitre, setSousTitre] = useState(evenementId ? '' : SOUS_TITRE_DEFAUT)
  const [sticker, setSticker] = useState(STICKERS[0])
  const [exclus, setExclus] = useState(() => new Set())
  const [url, setUrl] = useState(null)         // adresse effectivement affichee (debounce)
  const [chargement, setChargement] = useState(true)
  const [envoi, setEnvoi] = useState(false)
  const [flash, setFlash] = useState(null)

  const offres = fiche?.offers || []
  const evenements = fiche?.['prochainsMarchés'] || []
  const produitsGardes = offres.filter((o) => !exclus.has(o.id)).map((o) => o.id)
  const tactile = window.matchMedia?.('(pointer: coarse)').matches

  // Le serveur compose une image a chaque changement : on attend que la
  // personne ait fini de taper avant de la lui demander.
  useEffect(() => {
    if (!fiche) return
    const t = setTimeout(() => {
      setChargement(true)
      setUrl(urlVisuel({ id: producteurId, format, accroche, sousTitre, sticker, evenement, produitsGardes, total: offres.length }))
    }, 450)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiche, format, accroche, sousTitre, sticker, evenement, exclus])

  const choisirEvenement = (id) => {
    setEvenement(id)
    // Le texte generique n'a plus de sens avec une vraie date ; et inversement.
    setSousTitre(id ? '' : SOUS_TITRE_DEFAUT)
  }
  const basculer = (id) => setExclus((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const montrer = (msg) => { setFlash(msg); setTimeout(() => setFlash(null), 3200) }
  const partager = async () => {
    if (!url || envoi) return
    setEnvoi(true)
    const r = await partagerImage({ url, nomFichier: `lyannaj-${format}.png`, titre: fiche?.nomCommercial || fiche?.nom })
    setEnvoi(false)
    if (r === 'telecharge') montrer('✅ Image enregistrée dans vos téléchargements')
    else if (r === 'ouvert') montrer('Appuyez longtemps sur l’image pour l’enregistrer')
    else if (r === 'echec') montrer('Impossible de récupérer l’image, réessayez')
  }

  const ratio = FORMATS.find((f) => f.v === format).ratio

  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgb(var(--c-green) / 0.35)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="panel-slide-up"
        style={{ width: '100%', maxWidth: 430, height: '92vh', background: 'rgb(var(--c-creme))', borderRadius: '28px 28px 0 0', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 -10px 50px rgb(var(--c-green) / 0.3)' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px', borderBottom: bordure }}>
          <p style={{ flex: 1, fontFamily: "'Instrument Serif',serif", fontSize: 20, color: 'rgb(var(--c-green))' }}>Créer un visuel</p>
          <button onClick={onClose} aria-label="Fermer" style={{ background: 'none', border: 'none', fontSize: 24, color: 'rgb(var(--c-green) / 0.4)', cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {/* Apercu */}
          <div style={{ position: 'relative', width: format === 'story' ? '62%' : '82%', margin: '0 auto 14px', aspectRatio: ratio, borderRadius: 16, overflow: 'hidden', background: 'rgb(var(--c-green) / 0.08)', boxShadow: '0 8px 30px rgb(var(--c-green) / 0.15)' }}>
            {url && (
              <img src={url} alt="Aperçu du visuel" onLoad={() => setChargement(false)} onError={() => { setChargement(false); montrer('Aperçu indisponible') }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: chargement ? 0.35 : 1, transition: 'opacity .25s' }} />
            )}
            {chargement && (
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontSize: 12.5, fontWeight: 700, color: 'rgb(var(--c-green) / 0.7)' }}>
                Composition…
              </div>
            )}
          </div>

          {/* Format */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {FORMATS.map((f) => (
              <button key={f.v} onClick={() => setFormat(f.v)}
                style={{ flex: 1, padding: '9px 0', borderRadius: 999, fontSize: 13, fontWeight: 800, cursor: 'pointer', border: bordure,
                  background: format === f.v ? 'rgb(var(--c-primary))' : 'rgb(255 255 255 / 0.6)', color: 'rgb(var(--c-green))' }}>
                {f.l} <span style={{ opacity: 0.6, fontWeight: 600 }}>{f.s}</span>
              </button>
            ))}
          </div>

          {/* Evenement */}
          {evenements.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <span style={etiquette}>Évènement (date et lieu sur le visuel)</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {evenements.map((e) => (
                  <button key={e.id} onClick={() => choisirEvenement(e.id)}
                    style={{ padding: '6px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: bordure,
                      background: evenement === e.id ? 'rgb(var(--c-primary))' : 'rgb(255 255 255 / 0.6)', color: 'rgb(var(--c-green))' }}>
                    {formatDateCourte(e.date)} · {e.nom || e.lieu}
                  </button>
                ))}
                <button onClick={() => choisirEvenement('')}
                  style={{ padding: '6px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: bordure,
                    background: evenement === '' ? 'rgb(var(--c-primary))' : 'rgb(255 255 255 / 0.6)', color: 'rgb(var(--c-green))' }}>
                  Aucun
                </button>
              </div>
            </div>
          )}

          {/* Accroche */}
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={etiquette}>Accroche</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={accroche} onChange={(e) => setAccroche(e.target.value.slice(0, 80))} style={champ} />
              <button onClick={() => setAccroche((a) => accrocheAleatoire(a))} aria-label="Autre accroche" title="Une autre, au hasard"
                style={{ flexShrink: 0, width: 46, borderRadius: 13, border: bordure, background: 'rgb(255 255 255 / 0.7)', fontSize: 20, cursor: 'pointer' }}>🎲</button>
            </div>
          </label>

          {/* Rendez-vous */}
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={etiquette}>Rendez-vous</span>
            <input value={sousTitre} onChange={(e) => setSousTitre(e.target.value.slice(0, 60))}
              placeholder={evenement ? 'Lieu et horaires de l’évènement (automatique)' : 'Laissez vide pour ne rien afficher'} style={champ} />
          </label>

          {/* Sticker */}
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={etiquette}>Sticker rond</span>
            <input value={sticker} onChange={(e) => setSticker(e.target.value.slice(0, 24))} placeholder="Laissez vide pour ne pas en mettre" style={champ} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {STICKERS.map((t) => (
                <button key={t} onClick={() => setSticker(t)}
                  style={{ padding: '5px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: bordure,
                    background: sticker === t ? 'rgb(var(--c-primary))' : 'rgb(255 255 255 / 0.6)', color: 'rgb(var(--c-green))' }}>
                  {t}
                </button>
              ))}
              <button onClick={() => setSticker('')}
                style={{ padding: '5px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: bordure,
                  background: sticker === '' ? 'rgb(var(--c-primary))' : 'rgb(255 255 255 / 0.6)', color: 'rgb(var(--c-green))' }}>
                Aucun
              </button>
            </div>
          </label>

          {/* Produits */}
          {offres.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <span style={etiquette}>Produits affichés ({produitsGardes.length}/{offres.length}) — touchez pour retirer</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {offres.map((o) => {
                  const actif = !exclus.has(o.id)
                  return (
                    <button key={o.id} onClick={() => basculer(o.id)}
                      style={{ padding: '6px 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: bordure,
                        background: actif ? 'rgb(var(--c-teal) / 0.18)' : 'transparent', color: 'rgb(var(--c-green))', opacity: actif ? 1 : 0.45, textDecoration: actif ? 'none' : 'line-through' }}>
                      {o.product?.category?.emoji ? `${o.product.category.emoji} ` : ''}{o.product?.nom}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <p style={{ fontSize: 12, color: 'rgb(var(--c-green) / 0.6)', lineHeight: 1.4 }}>
            Publiez-la en story et identifiez <b>@manrina_mq</b> : on la repartage.
          </p>
        </div>

        <div style={{ padding: '12px 18px 22px', borderTop: bordure }}>
          {flash && <p style={{ fontSize: 12.5, fontWeight: 700, color: 'rgb(var(--c-green))', textAlign: 'center', marginBottom: 8 }}>{flash}</p>}
          <button onClick={partager} disabled={!url || chargement || envoi}
            style={{ width: '100%', padding: '14px 0', borderRadius: 999, border: 'none', fontSize: 14.5, fontWeight: 800, cursor: 'pointer',
              background: 'rgb(var(--c-primary))', color: 'rgb(var(--c-green))', opacity: !url || chargement || envoi ? 0.55 : 1 }}>
            {envoi ? 'Un instant…' : tactile ? '↗ Partager l’image' : '⬇️ Enregistrer l’image'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * Ouvreur (patron ADR-066) : `ouvrirModaleVisuel({ id, evenementId })` depuis
 * n'importe ou. Sans `id`, la modale prend l'organisation du compte connecte
 * (cas des cartes d'evenement, qui ne la connaissent pas).
 */
export function ouvrirModaleVisuel({ id, evenementId } = {}) {
  modales.ouvrir({
    id: `visuel-${id || 'moi'}-${evenementId || ''}`,
    contenu: ({ fermer }) => <ModaleVisuel producteurId={id} evenementId={evenementId} onClose={fermer} />,
  })
}

export default ModaleVisuel
